import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import type { Resolver } from "react-hook-form";
import { closestCenter, DndContext, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Input,
  RichTextEditor,
  Select,
  Toast,
  type ToastType,
  type SelectOption as DesignSystemSelectOption,
} from "@legal-500/legal500-design-system";
import {
  rollingSubmissionsService,
  type RollingSubmissionUpdatePayload,
  type RollingSubmissionCreatePayload,
} from "@/api/rollingSubmissions.service";
import * as api from "@/api/client";
import { companies, offices, contacts } from "@/api/addressBook.service";
import { editorialCategories } from "@/api/editorialCategories.service";
import { getLegacyPublications } from "@/api/legacy-publications.service";
import { useRollingSubmission } from "@/hooks/useRollingSubmissions";
import {
  AsyncSearchableSelect,
  type AsyncSelectOption,
} from "@/components/Form/AsyncSearchableSelect";
import type { SelectOption } from "@/components/Form/SearchableSelect/useSearchableSelect";
import LoadingState from "@/components/LoadingState";
import EmptyState from "@/components/EmptyState";
import { UnsavedChangesModal } from "@/components/Modal/UnsavedChangesModal";
import { useNavigationGuard } from "@/context/NavigationGuardContext";
import { ConfirmationModal } from "@/components/Modal/ConfirmationModal";
import { ROUTES } from "@/routes";
import s from "./RollingSubmissionDetail.module.css";
import {
  rollingSubmissionFormSchema,
  type RollingSubmissionFormValues,
  STATUS_OPTIONS,
  SUBMISSION_STATUS_OPTIONS,
  VISIBILITY_OPTIONS,
  ROLE_OPTIONS,
  PUBLISHABLE_OPTIONS,
  INDUSTRY_OPTIONS_FALLBACK,
  ROLE_LABELS,
  getInitials,
  getPendingCount,
} from "./schema";
import { SearchableSelect } from "@/components/Form/SearchableSelect/SearchableSelect";
import { STATUS_SELECT_CLASS } from "./constants";
import {
  ChevronSvg,
  GripIcon,
  SortableAccordionItem,
  AuditBlock,
} from "./components/AccordionHelpers";
import { StatusBadge } from "./components/StatusBadge";
import { useDragSensors } from "./hooks/useDragSensors";
import PermissionBanner from "@/components/Permissions/PermissionBanner";

function toLocalDatetimeString(isoString: string): string {
  const d = new Date(isoString);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function RollingSubmissionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isCreate = id === "new";
  const [toast, setToast] = useState<{
    message: string;
    type: ToastType;
  } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [savedOnce, setSavedOnce] = useState(false);
  const [manualDirty, setManualDirty] = useState(false);
  const dragSensors = useDragSensors();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<{
    section: "teamActive" | "teamDeparted" | "keyClients" | "workHighlights";
    index: number;
    label: string;
  } | null>(null);
  const [companyUuid, setCompanyUuid] = useState<string | null>(null);
  const [pendingNav, setPendingNav] = useState<(() => void) | null>(null);
  const [showPublishPrompt, setShowPublishPrompt] = useState(false);
  const publishPromptShownRef = useRef(false);
  const firmSearchResultsRef = useRef<
    Array<{
      companyId: number;
      id: string;
      name: string;
      firmRef: string | null;
      slug: string | null;
      crmEntityNumber: string | null;
    }>
  >([]);
  const [selectedCompanyDetails, setSelectedCompanyDetails] = useState<{
    slug: string | null;
    crmEntityNumber: string | null;
  } | null>(null);
  const practiceAreaCacheRef = useRef<{
    key: string;
    options: AsyncSelectOption[];
  }>({
    key: "",
    options: [],
  });
  const regionCacheRef = useRef<{ key: string; options: AsyncSelectOption[] }>({
    key: "",
    options: [],
  });
  const [selectedCountry, setSelectedCountry] = useState<{
    countryId: number;
    country: string;
  } | null>(null);
  const [officeOptions, setOfficeOptions] = useState<
    Array<{
      id: string;
      officeId: number;
      name: string;
      city: string | null;
      countryName: string | null;
      countryId: number | null;
    }>
  >([]);
  const [contactOptions, setContactOptions] = useState<
    Array<{ id: string; identityId: string; contactId: number; name: string; email: string | null }>
  >([]);
  const [industryOptions, setIndustryOptions] = useState<SelectOption[]>(INDUSTRY_OPTIONS_FALLBACK);

  const { data, loading, error, refetch } = useRollingSubmission(id ?? "");
  const submission = isCreate ? undefined : data?.submission;
  const [fullPublicationName, setFullPublicationName] = useState<string | null>(null);
  const [resolvedContact, setResolvedContact] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    if (!submission?.contactUuid) {
      setResolvedContact(null);
      return;
    }
    contacts.getById(submission.contactUuid).then(
      c => setResolvedContact({ id: c.data.id, name: c.data.name }),
      () => setResolvedContact(null),
    );
  }, [submission?.contactUuid]);

  useEffect(() => {
    if (!submission?.publicationId) return;
    getLegacyPublications({
      status: "Active,Researching,Inactive,Archive",
    }).then(
      res => {
        const pub = res.publications.find(p => p.id === submission.publicationId);
        if (pub) setFullPublicationName(`${pub.publication} (${pub.edition})`);
      },
      () => undefined,
    );
  }, [submission?.publicationId]);

  useEffect(() => {
    api
      .get<{ industrySectors: Array<{ id: string; wpId: number; name: string }> }>(
        "/v1/submission/industry-sectors",
      )
      .then(res => {
        setIndustryOptions([
          { value: "", label: "Select industry/sector" },
          ...res.industrySectors.map(s => ({
            value: s.id,
            label: s.name,
          })),
        ]);
      })
      .catch(err => {
        console.error("Failed to load industry sectors:", err);
        setToast({ message: "Failed to load industry sectors", type: "error" });
      });
  }, []);

  useEffect(() => {
    if (!submission?.companyUuid) {
      setCompanyUuid(null);
      return;
    }
    setCompanyUuid(submission.companyUuid);
  }, [submission?.companyUuid]);

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset,
    formState: { isDirty },
  } = useForm<RollingSubmissionFormValues>({
    resolver: zodResolver(
      rollingSubmissionFormSchema,
    ) as unknown as Resolver<RollingSubmissionFormValues>,
    defaultValues: {
      submissionStatus: "draft" as const,
      visibility: "public" as const,
      publishedAt: null,
      companyUuid: undefined,
      companyName: "",
      firmRef: "",
      officeUuid: undefined,
      officeName: "",
      publicationId: undefined,
      publicationName: "",
      practiceAreaId: undefined,
      practiceAreaName: "",
      contactUuid: undefined,
      contactName: "",
      teamActive: [] as RollingSubmissionFormValues["teamActive"],
      teamDeparted: [] as RollingSubmissionFormValues["teamDeparted"],
      keyClients: [] as RollingSubmissionFormValues["keyClients"],
      workHighlights: [] as RollingSubmissionFormValues["workHighlights"],
    },
  });

  const { setDirty: setNavDirty } = useNavigationGuard();

  const loadedSubmissionId = useRef<string | null>(null);
  useEffect(() => {
    if (!submission || loadedSubmissionId.current === submission.id) return;
    loadedSubmissionId.current = submission.id;
    reset({
      submissionStatus: submission.submissionStatus,
      visibility: submission.visibility,
      publishedAt: submission.publishedAt ? toLocalDatetimeString(submission.publishedAt) : null,
      companyUuid: undefined,
      companyName: "",
      firmRef: "",
      officeUuid: undefined,
      officeName: "",
      publicationId: undefined,
      publicationName: "",
      practiceAreaId: undefined,
      practiceAreaName: "",
      contactUuid: undefined,
      contactName: "",
      teamActive: submission.teamActive,
      teamDeparted: submission.teamDeparted,
      keyClients: submission.keyClients,
      workHighlights: submission.workHighlights,
    });
  }, [submission, reset]);

  useEffect(() => {
    function handleBeforeUnload(e: BeforeUnloadEvent) {
      if (isDirty || manualDirty) e.preventDefault();
    }
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty, manualDirty]);

  useEffect(() => {
    setNavDirty(isDirty || manualDirty);
    return () => setNavDirty(false);
  }, [isDirty, manualDirty, setNavDirty]);

  const requestNavigate = useCallback(
    (go: () => void) => {
      if (isDirty || manualDirty) {
        setPendingNav(() => go);
      } else {
        go();
      }
    },
    [isDirty, manualDirty],
  );

  const hasUnsavedRef = useRef(false);
  hasUnsavedRef.current = isDirty || manualDirty;
  const historyGuardPushed = useRef(false);

  useEffect(() => {
    if (hasUnsavedRef.current && !historyGuardPushed.current) {
      window.history.pushState({ guard: true }, "");
      historyGuardPushed.current = true;
    }

    function handlePopState() {
      if (hasUnsavedRef.current) {
        window.history.pushState({ guard: true }, "");
        setPendingNav(() => () => window.history.go(-2));
      } else {
        historyGuardPushed.current = false;
      }
    }

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isDirty, manualDirty]);

  const handleFirmSearch = useCallback(async (query: string): Promise<AsyncSelectOption[]> => {
    const res = await companies.getAll({ search: query, limit: 20 });
    firmSearchResultsRef.current = res.data.map(c => ({
      companyId: c.companyId,
      id: c.id,
      name: c.name,
      firmRef: c.firmRef,
      slug: c.slug,
      crmEntityNumber: c.crmEntityNumber,
    }));
    return res.data.map(c => ({
      value: c.id,
      label: c.name,
      description: c.firmRef ?? undefined,
    }));
  }, []);

  useEffect(() => {
    if (!companyUuid) {
      setOfficeOptions([]);
      return;
    }
    offices.getAll({ companyId: companyUuid, limit: 100 }).then(
      res =>
        setOfficeOptions(
          res.data.map(o => ({
            id: o.id,
            officeId: o.officeId,
            name: o.name,
            city: o.city,
            countryName: o.countryName,
            countryId: o.countryId,
          })),
        ),
      () => setOfficeOptions([]),
    );
  }, [companyUuid]);

  const watchedOfficeUuid = watch("officeUuid");
  useEffect(() => {
    if (!companyUuid) {
      setContactOptions([]);
      return;
    }
    const filters: { companyId: string; officeId?: string; limit: number } = {
      companyId: companyUuid,
      limit: 100,
    };
    if (watchedOfficeUuid) {
      filters.officeId = watchedOfficeUuid;
    }
    contacts.getAll(filters).then(
      res =>
        setContactOptions(
          res.data.map(c => ({
            id: c.id,
            identityId: c.identityId,
            contactId: c.contactId,
            name: c.name,
            email: c.email,
          })),
        ),
      () => setContactOptions([]),
    );
  }, [companyUuid, watchedOfficeUuid]);

  const [publicationOptions, setPublicationOptions] = useState<DesignSystemSelectOption[]>([]);
  useEffect(() => {
    if (!isCreate) return;
    getLegacyPublications({ limit: 100 }).then(
      res => {
        const pubs = [...res.publications].sort((a, b) =>
          a.publication.localeCompare(b.publication),
        );
        setPublicationOptions([
          { value: "", label: "Select publication..." },
          ...pubs.map(p => ({
            value: String(p.id),
            label: `${p.publication} (${p.edition})`,
          })),
        ]);
      },
      () => setPublicationOptions([]),
    );
  }, []);

  const {
    fields: teamActiveFields,
    append: appendTeamActive,
    remove: removeTeamActive,
    move: moveTeamActive,
  } = useFieldArray({ control, name: "teamActive" });
  const {
    fields: teamDepartedFields,
    append: appendTeamDeparted,
    remove: removeTeamDeparted,
    move: moveTeamDeparted,
  } = useFieldArray({ control, name: "teamDeparted" });
  const {
    fields: keyClientsFields,
    append: appendKeyClient,
    remove: removeKeyClient,
    move: moveKeyClient,
  } = useFieldArray({ control, name: "keyClients" });
  const {
    fields: workHighlightsFields,
    append: appendWorkHighlight,
    remove: removeWorkHighlight,
    move: moveWorkHighlight,
  } = useFieldArray({ control, name: "workHighlights" });

  const watchedTeamActive = watch("teamActive") ?? [];
  const watchedTeamDeparted = watch("teamDeparted") ?? [];
  const watchedKeyClients = watch("keyClients") ?? [];
  const watchedWorkHighlights = watch("workHighlights") ?? [];

  const handleDragEndTeamActive = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = teamActiveFields.findIndex(f => f.id === active.id);
      const newIndex = teamActiveFields.findIndex(f => f.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        moveTeamActive(oldIndex, newIndex);
        setManualDirty(true);
      }
    },
    [teamActiveFields, moveTeamActive],
  );

  const handleDragEndTeamDeparted = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = teamDepartedFields.findIndex(f => f.id === active.id);
      const newIndex = teamDepartedFields.findIndex(f => f.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        moveTeamDeparted(oldIndex, newIndex);
        setManualDirty(true);
      }
    },
    [teamDepartedFields, moveTeamDeparted],
  );

  const handleDragEndKeyClients = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = keyClientsFields.findIndex(f => f.id === active.id);
      const newIndex = keyClientsFields.findIndex(f => f.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        moveKeyClient(oldIndex, newIndex);
        setManualDirty(true);
      }
    },
    [keyClientsFields, moveKeyClient],
  );

  const handleDragEndWorkHighlights = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = workHighlightsFields.findIndex(f => f.id === active.id);
      const newIndex = workHighlightsFields.findIndex(f => f.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        moveWorkHighlight(oldIndex, newIndex);
        setManualDirty(true);
      }
    },
    [workHighlightsFields, moveWorkHighlight],
  );

  const _handleAddTeamActive = useCallback(() => {
    const newId = crypto.randomUUID();
    appendTeamActive({
      id: newId,
      name: "",
      role: "",
      jobTitle: "",
      email: "",
      location: "",
      dateJoined: null,
      previousFirm: null,
      created: null,
      modified: null,
      modifiedBy: null,
      status: "pending",
    });
    setManualDirty(true);
    setNewItemId(newId);
  }, [appendTeamActive]);

  const watchedSubmissionStatus = watch("submissionStatus");

  const [newItemId, setNewItemId] = useState<string | null>(null);

  useEffect(() => {
    if (!newItemId) return;
    const el = document.querySelector(`[data-item-id="${newItemId}"]`);
    if (el instanceof HTMLDetailsElement) {
      el.open = true;
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
    setNewItemId(null);
  }, [newItemId]);

  const handleApproveAll = useCallback(() => {
    const current = getValues();
    const opts = { shouldDirty: true } as const;
    setValue(
      "teamActive",
      current.teamActive.map(item => ({
        ...item,
        status: "publish" as const,
      })),
      opts,
    );
    setValue(
      "teamDeparted",
      current.teamDeparted.map(item => ({
        ...item,
        status: "publish" as const,
      })),
      opts,
    );
    setValue(
      "keyClients",
      current.keyClients.map(item => ({
        ...item,
        status: "publish" as const,
      })),
      opts,
    );
    setValue(
      "workHighlights",
      current.workHighlights.map(item => ({
        ...item,
        status: "publish" as const,
      })),
      opts,
    );
  }, [getValues, setValue]);

  const confirmRemove = () => {
    if (!pendingRemove) return;
    const { section, index } = pendingRemove;
    if (section === "teamActive") removeTeamActive(index);
    else if (section === "teamDeparted") removeTeamDeparted(index);
    else if (section === "keyClients") removeKeyClient(index);
    else if (section === "workHighlights") removeWorkHighlight(index);
    setManualDirty(true);
    setPendingRemove(null);
  };

  const onSubmit = async (formData: RollingSubmissionFormValues) => {
    if (!id) return;
    if (!publishPromptShownRef.current && !isCreate) {
      const allItems = [
        ...formData.teamActive,
        ...formData.teamDeparted,
        ...formData.keyClients,
        ...formData.workHighlights,
      ];
      if (
        allItems.length > 0 &&
        allItems.every(item => item.status === "publish") &&
        formData.submissionStatus !== "publish"
      ) {
        publishPromptShownRef.current = true;
        setShowPublishPrompt(true);
        return;
      }
    }
    setIsSaving(true);
    try {
      if (isCreate) {
        if (!formData.companyUuid || !formData.publicationId || !formData.practiceAreaId) {
          setToast({
            message: "Firm, Publication, and Practice Area are required",
            type: "error",
          });
          return;
        }
        const createPayload: RollingSubmissionCreatePayload = {
          companyUuid: formData.companyUuid,
          companyName: formData.companyName || undefined,
          firmRef: formData.firmRef || undefined,
          officeUuid: formData.officeUuid,
          officeName: formData.officeName || undefined,
          publicationId: formData.publicationId,
          publicationName: formData.publicationName || undefined,
          countryId: selectedCountry?.countryId,
          country: selectedCountry?.country,
          practiceAreaId: formData.practiceAreaId,
          practiceAreaName: formData.practiceAreaName || undefined,
          contactUuid: formData.contactUuid,
          status: formData.submissionStatus,
          teamActive: formData.teamActive,
          teamDeparted: formData.teamDeparted,
          keyClients: formData.keyClients,
          workHighlights: formData.workHighlights,
        };
        const result = await rollingSubmissionsService.create(createPayload);
        setToast({
          message: "Submission created successfully",
          type: "success",
        });
        navigate(ROUTES.submissions.rollingSubmissionDetail(result.submission.id), {
          replace: true,
        });
      } else {
        const isTransitioningToPublish =
          formData.submissionStatus === "publish" && submission?.submissionStatus !== "publish";
        const publishedAt =
          formData.submissionStatus === "publish" &&
          (!formData.publishedAt?.trim() || isTransitioningToPublish)
            ? new Date().toISOString()
            : formData.publishedAt || null;
        // Normalise local datetime strings (from datetime-local input) to UTC ISO
        const normalizedPublishedAt =
          publishedAt && !publishedAt.endsWith("Z") && !publishedAt.includes("+")
            ? new Date(publishedAt).toISOString()
            : publishedAt;
        const payload: RollingSubmissionUpdatePayload = {
          submissionStatus: formData.submissionStatus,
          visibility: formData.visibility,
          publishedAt: normalizedPublishedAt,
          teamActive: formData.teamActive,
          teamDeparted: formData.teamDeparted,
          keyClients: formData.keyClients,
          workHighlights: formData.workHighlights,
        };
        await rollingSubmissionsService.update(id, payload);
        if (formData.submissionStatus === "publish" && submission?.submissionStatus !== "publish") {
          await api.patch(`/v1/submission/rolling-submissions/${id}/approve`, {
            approvalStatus: "publish",
          });
        }
        reset(getValues());
        setManualDirty(false);
        await refetch();
        setSavedOnce(true);
        setToast({ message: "All changes saved", type: "success" });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save changes";
      setToast({ message, type: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    await rollingSubmissionsService.delete(id);
    navigate(ROUTES.submissions.rollingSubmissions);
  };

  if (!isCreate) {
    if (loading) return <LoadingState message="Loading submission details..." />;
    if (error)
      return (
        <EmptyState
          variant="error"
          title="Failed to load submission"
          message={error.message}
          action={{
            label: "Go back",
            onClick: () => navigate(ROUTES.submissions.rollingSubmissions),
          }}
        />
      );
    if (!submission)
      return (
        <EmptyState
          variant="empty"
          title="Submission not found"
          message="The requested rolling submission could not be found."
          action={{
            label: "Go back",
            onClick: () => navigate(ROUTES.submissions.rollingSubmissions),
          }}
        />
      );
  }

  const firmLink = companyUuid ? ROUTES.addressBook.companyDetail(companyUuid) : null;
  const submissionOfficeUuid = submission?.officeUuid ?? null;
  const officeLink = submissionOfficeUuid
    ? ROUTES.addressBook.officeDetail(submissionOfficeUuid)
    : null;
  const totalPending =
    getPendingCount(watchedTeamActive) +
    getPendingCount(watchedTeamDeparted) +
    getPendingCount(watchedKeyClients) +
    getPendingCount(watchedWorkHighlights);

  return (
    <div className={s.detailWrapper}>
      <PermissionBanner scope="submissions" />
      <div className={s.headerRegion}>
        <button
          type="button"
          className={s.backLink}
          onClick={() => requestNavigate(() => navigate(ROUTES.submissions.rollingSubmissions))}
        >
          &larr; Back to Rolling Submissions
        </button>

        {isCreate ? (
          <div className={s.headerSection}>
            <div className={s.headerMain}>
              <div className={s.headerLeft}>
                <h1 className={s.headerTitle}>New Rolling Submission</h1>
              </div>
            </div>
          </div>
        ) : (
          <div className={s.headerSection}>
            <div className={s.headerMain}>
              <div className={s.headerLeft}>
                <span className={s.headerLabel}>Firm</span>
                <div className={s.headerTitleRow}>
                  {firmLink ? (
                    <Link
                      to={firmLink}
                      className={s.headerTitleLink}
                      onClick={e => {
                        if (isDirty || manualDirty) {
                          e.preventDefault();
                          requestNavigate(() => navigate(firmLink));
                        }
                      }}
                    >
                      <h1 className={s.headerTitle}>{submission?.firmName}</h1>
                    </Link>
                  ) : (
                    <h1 className={s.headerTitle}>{submission?.firmName}</h1>
                  )}
                </div>
                {submission?.officeName &&
                  (officeLink ? (
                    <Link
                      to={officeLink}
                      className={s.headerOfficeLink}
                      onClick={e => {
                        if (isDirty || manualDirty) {
                          e.preventDefault();
                          requestNavigate(() => navigate(officeLink));
                        }
                      }}
                    >
                      {submission.officeName}
                    </Link>
                  ) : (
                    <span className={s.headerOffice}>{submission.officeName}</span>
                  ))}
              </div>
              <div className={s.headerActions}>
                {totalPending > 0 && (
                  <span className={s.pendingBadge}>
                    <span className={s.pendingBadgeDot} />
                    {totalPending} pending
                  </span>
                )}
                <button type="button" className={s.approveAllBtn} onClick={handleApproveAll}>
                  Publish All
                </button>
              </div>
            </div>
            <div className={s.metaStrip}>
              <div className={s.metaField}>
                <span className={s.metaLabel}>Publication</span>
                <span className={s.metaValue}>
                  {fullPublicationName ?? submission?.publication}
                </span>
                {submission?.publicationId && (
                  <span className={s.metaId}>#{submission.publicationId}</span>
                )}
              </div>
              <div className={s.metaField}>
                <span className={s.metaLabel}>Region</span>
                <span className={s.metaValue}>
                  {submission?.country ?? submission?.region ?? "—"}
                </span>
                {submission?.regionId && <span className={s.metaId}>#{submission.regionId}</span>}
              </div>
              <div
                className={
                  submission?.contactUuid ? s.metaField : `${s.metaField} ${s.metaFieldLast}`
                }
              >
                <span className={s.metaLabel}>Practice Area</span>
                <span className={s.metaValue}>
                  {submission?.practiceGroup
                    ? `${submission.practiceGroup} > ${submission.practiceArea}`
                    : submission?.practiceArea}
                </span>
                {submission?.practiceAreaId && (
                  <span className={s.metaId}>#{submission.practiceAreaId}</span>
                )}
              </div>
              {submission?.contactUuid && (
                <div className={`${s.metaField} ${s.metaFieldLast}`}>
                  <span className={s.metaLabel}>Contact</span>
                  <Link
                    to={ROUTES.addressBook.contactDetail(
                      resolvedContact?.id ?? submission.contactUuid,
                    )}
                    className={s.metaValue}
                    onClick={e => {
                      if (isDirty || manualDirty) {
                        e.preventDefault();
                        requestNavigate(() =>
                          navigate(
                            ROUTES.addressBook.contactDetail(
                              resolvedContact?.id ?? submission.contactUuid!,
                            ),
                          ),
                        );
                      }
                    }}
                  >
                    {resolvedContact?.name ?? submission.contactUuid}
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}

        <div className={s.publishCard}>
          <div className={s.publishGrid}>
            <div className={s.publishField}>
              <label className={s.publishLabel}>Status</label>
              <Controller
                control={control}
                name="submissionStatus"
                render={({ field: f }) => (
                  <Select options={SUBMISSION_STATUS_OPTIONS} size="small" {...f} />
                )}
              />
            </div>
            <div className={s.publishField}>
              <label className={s.publishLabel}>Visibility</label>
              <Controller
                control={control}
                name="visibility"
                render={({ field: f }) => (
                  <Select options={VISIBILITY_OPTIONS} size="small" {...f} />
                )}
              />
            </div>
            <div className={s.publishField}>
              <label className={s.publishLabel}>Published on</label>
              <Controller
                control={control}
                name="publishedAt"
                render={({ field: f }) => (
                  <Input
                    type="datetime-local"
                    value={f.value ?? ""}
                    onChange={e => f.onChange(e.target.value || null)}
                    onBlur={f.onBlur}
                  />
                )}
              />
            </div>
          </div>
        </div>
      </div>

      {!isCreate &&
        submission &&
        (watchedTeamActive.some(i => i.status === "pending") ||
          watchedTeamDeparted.some(i => i.status === "pending") ||
          watchedKeyClients.some(i => i.status === "pending") ||
          watchedWorkHighlights.some(i => i.status === "pending")) && (
          <div className={s.infoBanner}>
            This submission has fields pending approval. Pending items will not appear on the live
            site until their status is changed to Publish.
            {watchedSubmissionStatus !== "publish" &&
              " The overall submission status must also be set to Published."}
          </div>
        )}

      <form
        className={s.formContent}
        onSubmit={e =>
          void handleSubmit(onSubmit, errors => {
            const fields = Object.keys(errors).join(", ");
            setToast({ message: `Validation failed: ${fields}`, type: "error" });
          })(e)
        }
        onChange={() => setManualDirty(true)}
      >
        {isCreate && (
          <div className={s.sectionCard}>
            <div className={s.sectionHeader}>
              <h3 className={s.sectionTitle}>Submission Details</h3>
            </div>
            <div className={s.createFieldsBody}>
              <div className={s.createFieldsGrid}>
                <div className={s.fieldSpan2}>
                  <AsyncSearchableSelect
                    name="firm"
                    label="Firm"
                    required
                    value={watch("companyUuid") ?? null}
                    onSearch={handleFirmSearch}
                    onChange={() => undefined}
                    onOptionChange={option => {
                      const opts = { shouldDirty: true } as const;
                      if (option) {
                        const company = firmSearchResultsRef.current.find(
                          c => c.id === option.value,
                        );
                        setValue("companyUuid", String(option.value), opts);
                        setValue("companyName", option.label, opts);
                        setValue("firmRef", company?.firmRef ?? "", opts);
                        setCompanyUuid(String(option.value));
                        setSelectedCompanyDetails({
                          slug: company?.slug ?? null,
                          crmEntityNumber: company?.crmEntityNumber ?? null,
                        });
                        setValue("officeUuid", undefined, opts);
                        setValue("officeName", "", opts);
                        setValue("contactUuid", undefined, opts);
                        setValue("contactName", "", opts);
                        setValue("practiceAreaId", undefined, opts);
                        setValue("practiceAreaName", "", opts);
                      } else {
                        setValue("companyUuid", undefined, opts);
                        setValue("companyName", "", opts);
                        setValue("firmRef", "", opts);
                        setCompanyUuid(null);
                        setSelectedCompanyDetails(null);
                        setValue("officeUuid", undefined, opts);
                        setValue("officeName", "", opts);
                        setValue("contactUuid", undefined, opts);
                        setValue("contactName", "", opts);
                        setValue("practiceAreaId", undefined, opts);
                        setValue("practiceAreaName", "", opts);
                      }
                    }}
                    placeholder="Search for a firm..."
                  />
                </div>
                {watch("companyUuid") && (
                  <div className={s.companyInfoGrid}>
                    <div className={s.companyInfoItem}>
                      <span className={s.companyInfoLabel}>Company Slug</span>
                      <span className={s.companyInfoValue}>
                        {selectedCompanyDetails?.slug || (
                          <span className={s.companyInfoNull}>Not set</span>
                        )}
                      </span>
                    </div>
                    <div className={s.companyInfoItem}>
                      <span className={s.companyInfoLabel}>CRM Account ID</span>
                      <span className={s.companyInfoValue}>
                        {selectedCompanyDetails?.crmEntityNumber || (
                          <span className={s.companyInfoNull}>Not set</span>
                        )}
                      </span>
                    </div>
                  </div>
                )}
                <div className={s.field}>
                  <label className={s.fieldLabel}>Office</label>
                  <Select
                    options={[
                      {
                        value: "",
                        label: companyUuid ? "Select office..." : "Select a firm first",
                      },
                      ...officeOptions.map(o => {
                        const parts = [o.name];
                        if (o.city) parts.push(o.city);
                        if (o.countryName) parts.push(o.countryName);
                        return {
                          value: o.id,
                          label: parts.join(", "),
                        };
                      }),
                    ]}
                    value={watch("officeUuid") ?? ""}
                    disabled={!companyUuid}
                    onChange={e => {
                      const val = e.target.value;
                      const opts = { shouldDirty: true } as const;
                      if (val && val !== "") {
                        setValue("officeUuid", val, opts);
                        const office = officeOptions.find(o => o.id === val);
                        setValue("officeName", office?.name ?? "", opts);
                      } else {
                        setValue("officeUuid", undefined, opts);
                        setValue("officeName", "", opts);
                      }
                      setValue("practiceAreaId", undefined, opts);
                      setValue("practiceAreaName", "", opts);
                      setValue("contactUuid", undefined, opts);
                      setValue("contactName", "", opts);
                    }}
                  />
                </div>
                <div className={s.field}>
                  <AsyncSearchableSelect
                    name="publication"
                    label="Publication"
                    required
                    value={watch("publicationId") ?? null}
                    onSearch={async (query: string) => {
                      const q = query.toLowerCase();
                      return publicationOptions
                        .filter(p => p.value !== "" && (!q || p.label.toLowerCase().includes(q)))
                        .map(p => ({
                          value: Number(p.value),
                          label: p.label,
                        }));
                    }}
                    onChange={() => undefined}
                    onOptionChange={option => {
                      const opts = { shouldDirty: true } as const;
                      if (option) {
                        setValue("publicationId", Number(option.value), opts);
                        setValue("publicationName", option.label, opts);
                      } else {
                        setValue("publicationId", undefined, opts);
                        setValue("publicationName", "", opts);
                      }
                      setSelectedCountry(null);
                      regionCacheRef.current = { key: "", options: [] };
                      setValue("practiceAreaId", undefined, opts);
                      setValue("practiceAreaName", "", opts);
                      practiceAreaCacheRef.current = { key: "", options: [] };
                    }}
                    placeholder="Search for a publication..."
                    debounceMs={0}
                    minSearchLength={0}
                  />
                </div>
                <div className={s.field}>
                  <AsyncSearchableSelect
                    key={`region-${watch("publicationId") ?? "none"}`}
                    name="region"
                    label="Region"
                    required
                    value={selectedCountry?.countryId ?? null}
                    disabled={!watch("publicationId")}
                    onSearch={async (query: string) => {
                      const pubId = watch("publicationId");
                      if (!pubId) return [];
                      const cacheKey = `${pubId}`;
                      if (regionCacheRef.current.key !== cacheKey) {
                        try {
                          const res = await editorialCategories.getAll({
                            publicationId: pubId,
                            type: "country",
                            limit: 100,
                          });
                          const seen = new Set<number>();
                          const deduped: AsyncSelectOption[] = [];
                          for (const ec of res.editorialCategories ?? []) {
                            if (ec.countryId != null && !seen.has(ec.countryId)) {
                              seen.add(ec.countryId);
                              deduped.push({
                                value: ec.countryId,
                                label: ec.country ?? "",
                              });
                            }
                          }
                          regionCacheRef.current = {
                            key: cacheKey,
                            options: deduped,
                          };
                        } catch (err) {
                          console.error("Failed to load regions:", err);
                          return [];
                        }
                      }
                      const q = query.toLowerCase();
                      return regionCacheRef.current.options.filter(
                        r => !q || r.label.toLowerCase().includes(q),
                      );
                    }}
                    onChange={() => undefined}
                    onOptionChange={option => {
                      if (option) {
                        setSelectedCountry({
                          countryId: Number(option.value),
                          country: option.label,
                        });
                      } else {
                        setSelectedCountry(null);
                      }
                      const opts = { shouldDirty: true } as const;
                      setValue("practiceAreaId", undefined, opts);
                      setValue("practiceAreaName", "", opts);
                      practiceAreaCacheRef.current = { key: "", options: [] };
                    }}
                    placeholder={
                      watch("publicationId")
                        ? "Search for a region..."
                        : "Select a publication first"
                    }
                    debounceMs={0}
                    minSearchLength={0}
                  />
                </div>
                <div className={s.field}>
                  <AsyncSearchableSelect
                    key={`pa-${watch("publicationId") ?? "none"}:${selectedCountry?.countryId ?? "none"}`}
                    name="practiceArea"
                    label="Practice Area"
                    required
                    value={watch("practiceAreaId") ?? null}
                    disabled={!watch("publicationId") || !selectedCountry}
                    onSearch={async (query: string) => {
                      const pubId = watch("publicationId");
                      const countryId = selectedCountry?.countryId;
                      if (!pubId || !countryId) return [];
                      const cacheKey = `${pubId}:${countryId}`;
                      if (practiceAreaCacheRef.current.key !== cacheKey) {
                        try {
                          const res = await editorialCategories.getSubmissionBpas(pubId, countryId);
                          practiceAreaCacheRef.current = {
                            key: cacheKey,
                            options: (res.practiceAreas ?? []).map(pa => ({
                              value: pa.id,
                              label: pa.practiceGroupName
                                ? `${pa.practiceGroupName} > ${pa.name}`
                                : pa.name,
                            })),
                          };
                        } catch (err) {
                          console.error("Failed to load practice areas:", err);
                          return [];
                        }
                      }
                      const q = query.toLowerCase();
                      return practiceAreaCacheRef.current.options.filter(
                        pa => !q || pa.label.toLowerCase().includes(q),
                      );
                    }}
                    onChange={() => undefined}
                    onOptionChange={option => {
                      const opts = { shouldDirty: true } as const;
                      if (option) {
                        setValue("practiceAreaId", Number(option.value), opts);
                        setValue("practiceAreaName", option.label, opts);
                      } else {
                        setValue("practiceAreaId", undefined, opts);
                        setValue("practiceAreaName", "", opts);
                      }
                    }}
                    placeholder={
                      watch("publicationId") && selectedCountry
                        ? "Search for a practice area..."
                        : "Select a publication and region first"
                    }
                    debounceMs={0}
                    minSearchLength={0}
                  />
                </div>
                <div className={s.field}>
                  <label className={s.fieldLabel}>Contact</label>
                  <Select
                    options={[
                      {
                        value: "",
                        label: companyUuid ? "Select contact..." : "Select a firm first",
                      },
                      ...contactOptions.map(c => ({
                        value: c.id,
                        label: c.email ? `${c.name} (${c.email})` : c.name,
                      })),
                    ]}
                    value={watch("contactUuid") ?? ""}
                    disabled={!companyUuid}
                    onChange={e => {
                      const val = e.target.value;
                      const opts = { shouldDirty: true } as const;
                      if (val && val !== "") {
                        setValue("contactUuid", val, opts);
                        const contact = contactOptions.find(c => c.id === val);
                        setValue("contactName", contact?.name ?? "", opts);
                      } else {
                        setValue("contactUuid", undefined, opts);
                        setValue("contactName", "", opts);
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* -- Active Lawyers -- */}
        <div className={s.sectionCard}>
          <div className={s.sectionHeader}>
            <h3 className={s.sectionTitle}>Active Lawyers</h3>
            <span className={s.countBadge}>{teamActiveFields.length}</span>
            {getPendingCount(watchedTeamActive) > 0 && (
              <span className={s.pendingText}>
                &middot; {getPendingCount(watchedTeamActive)} pending
              </span>
            )}
            <button
              type="button"
              className={s.addBtn}
              onClick={() => {
                const newId = crypto.randomUUID();
                appendTeamActive({
                  id: newId,
                  name: "",
                  role: "",
                  jobTitle: "",
                  email: "",
                  location: "",
                  dateJoined: null,
                  previousFirm: null,
                  created: new Date().toISOString(),
                  modified: null,
                  modifiedBy: null,
                  status: "pending",
                });
                setManualDirty(true);
                setNewItemId(newId);
              }}
            >
              + Add lawyer
            </button>
          </div>
          {teamActiveFields.length === 0 ? (
            <p className={s.emptySection}>No active lawyers in this submission</p>
          ) : (
            <DndContext
              sensors={dragSensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEndTeamActive}
            >
              <SortableContext
                items={teamActiveFields.map(f => f.id)}
                strategy={verticalListSortingStrategy}
              >
                {teamActiveFields.map((field, i) => {
                  const isPending = watchedTeamActive[i]?.status === "pending";
                  const status = watchedTeamActive[i]?.status ?? "draft";
                  return (
                    <SortableAccordionItem key={field.id} id={field.id}>
                      {({ listeners, attributes }) => (
                        <details
                          className={`${s.accordion} ${isPending ? s.accordionPending : ""}`}
                          data-item-id={watchedTeamActive[i]?.id ?? field.id}
                          {...(isPending ? { open: true } : {})}
                        >
                          <summary>
                            <div className={s.accordionSummary}>
                              <span
                                className={s.dragHandle}
                                title="Drag to reorder"
                                {...listeners}
                                {...attributes}
                              >
                                <GripIcon />
                              </span>
                              <ChevronSvg />
                              <span className={s.avatar}>
                                {getInitials(watchedTeamActive[i]?.name ?? field.name)}
                              </span>
                              <span className={s.itemName}>
                                {watchedTeamActive[i]?.name ?? field.name}
                              </span>
                              {(() => {
                                const rolePart =
                                  ROLE_LABELS[watchedTeamActive[i]?.role ?? field.role];
                                const loc = watchedTeamActive[i]?.location ?? field.location;
                                const locationPart =
                                  officeOptions.find(o => String(o.officeId) === loc)?.name ?? loc;
                                const parts = [rolePart, locationPart].filter(Boolean);
                                return parts.length > 0 ? (
                                  <span className={s.itemSubtitle}>{parts.join(" · ")}</span>
                                ) : null;
                              })()}
                              <StatusBadge status={status} />
                            </div>
                          </summary>
                          <div className={s.accordionBody}>
                            <div className={s.fieldGrid}>
                              <div className={s.field}>
                                <label className={s.fieldLabel}>Name</label>
                                <Controller
                                  control={control}
                                  name={`teamActive.${i}.name`}
                                  render={({ field: f }) => (
                                    <Input className={s.cellInput} {...f} />
                                  )}
                                />
                              </div>
                              <div className={s.field}>
                                <label className={s.fieldLabel}>Role</label>
                                <Controller
                                  control={control}
                                  name={`teamActive.${i}.role`}
                                  render={({ field: f }) => (
                                    <Select
                                      className={s.cellSelect}
                                      options={ROLE_OPTIONS}
                                      size="small"
                                      {...f}
                                    />
                                  )}
                                />
                              </div>
                              <div className={s.field}>
                                <label className={s.fieldLabel}>Job title</label>
                                <Controller
                                  control={control}
                                  name={`teamActive.${i}.jobTitle`}
                                  render={({ field: f }) => (
                                    <Input className={s.cellInput} {...f} />
                                  )}
                                />
                              </div>
                              <div className={s.field}>
                                <label className={s.fieldLabel}>Location</label>
                                <Controller
                                  control={control}
                                  name={`teamActive.${i}.location`}
                                  render={({ field: f }) => (
                                    <Select
                                      options={[
                                        {
                                          value: "",
                                          label: "Select location...",
                                        },
                                        ...officeOptions.map(o => {
                                          const parts = [o.name];
                                          if (o.city) parts.push(o.city);
                                          if (o.countryName) parts.push(o.countryName);
                                          return {
                                            value: String(o.officeId),
                                            label: parts.join(", "),
                                          };
                                        }),
                                      ]}
                                      value={f.value ?? ""}
                                      onChange={e => f.onChange(e.target.value)}
                                      onBlur={f.onBlur}
                                    />
                                  )}
                                />
                              </div>
                              <div className={`${s.field} ${s.fieldSpan2}`}>
                                <label className={s.fieldLabel}>Email</label>
                                <Controller
                                  control={control}
                                  name={`teamActive.${i}.email`}
                                  render={({ field: f }) => (
                                    <Input className={s.cellInput} type="email" {...f} />
                                  )}
                                />
                              </div>
                              <div className={s.field}>
                                <label className={s.fieldLabel}>Date joined</label>
                                <Controller
                                  control={control}
                                  name={`teamActive.${i}.dateJoined`}
                                  render={({ field: f }) => (
                                    <Input
                                      className={s.cellInput}
                                      type="date"
                                      value={f.value ?? ""}
                                      onChange={e => f.onChange(e.target.value || null)}
                                      onBlur={f.onBlur}
                                    />
                                  )}
                                />
                              </div>
                              <div className={s.field}>
                                <label className={s.fieldLabel}>Previous firm</label>
                                <Controller
                                  control={control}
                                  name={`teamActive.${i}.previousFirm`}
                                  render={({ field: f }) => (
                                    <Input
                                      className={s.cellInput}
                                      value={f.value ?? ""}
                                      onChange={e => f.onChange(e.target.value || null)}
                                      onBlur={f.onBlur}
                                    />
                                  )}
                                />
                              </div>
                              <div className={s.footerRow}>
                                <AuditBlock
                                  created={field.created}
                                  modified={field.modified}
                                  modifiedBy={field.modifiedBy}
                                />
                                <button
                                  type="button"
                                  className={s.removeBtn}
                                  onClick={() =>
                                    setPendingRemove({
                                      section: "teamActive",
                                      index: i,
                                      label: field.name || "this lawyer",
                                    })
                                  }
                                >
                                  Remove
                                </button>
                                <span className={s.statusLabel}>Status</span>
                                <Controller
                                  control={control}
                                  name={`teamActive.${i}.status`}
                                  render={({ field: f }) => (
                                    <Select
                                      className={`${s.statusSelect} ${STATUS_SELECT_CLASS[f.value] ?? ""}`}
                                      options={STATUS_OPTIONS}
                                      size="small"
                                      {...f}
                                    />
                                  )}
                                />
                              </div>
                            </div>
                          </div>
                        </details>
                      )}
                    </SortableAccordionItem>
                  );
                })}
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* -- Departed Lawyers -- */}
        <div className={s.sectionCard}>
          <div className={s.sectionHeader}>
            <h3 className={s.sectionTitle}>Departed Lawyers</h3>
            <span className={s.countBadge}>{teamDepartedFields.length}</span>
            {getPendingCount(watchedTeamDeparted) > 0 && (
              <span className={s.pendingText}>
                &middot; {getPendingCount(watchedTeamDeparted)} pending
              </span>
            )}
            <button
              type="button"
              className={s.addBtn}
              onClick={() => {
                const newId = crypto.randomUUID();
                appendTeamDeparted({
                  id: newId,
                  name: "",
                  role: "",
                  jobTitle: "",
                  dateLeft: null,
                  firmJoined: null,
                  created: new Date().toISOString(),
                  modified: null,
                  modifiedBy: null,
                  status: "pending",
                });
                setManualDirty(true);
                setNewItemId(newId);
              }}
            >
              + Add lawyer
            </button>
          </div>
          {teamDepartedFields.length === 0 ? (
            <p className={s.emptySection}>No departed lawyers in this submission</p>
          ) : (
            <DndContext
              sensors={dragSensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEndTeamDeparted}
            >
              <SortableContext
                items={teamDepartedFields.map(f => f.id)}
                strategy={verticalListSortingStrategy}
              >
                {teamDepartedFields.map((field, i) => {
                  const isPending = watchedTeamDeparted[i]?.status === "pending";
                  const status = watchedTeamDeparted[i]?.status ?? "draft";
                  return (
                    <SortableAccordionItem key={field.id} id={field.id}>
                      {({ listeners, attributes }) => (
                        <details
                          className={`${s.accordion} ${isPending ? s.accordionPending : ""}`}
                          data-item-id={watchedTeamDeparted[i]?.id ?? field.id}
                          {...(isPending ? { open: true } : {})}
                        >
                          <summary>
                            <div className={s.accordionSummary}>
                              <span
                                className={s.dragHandle}
                                title="Drag to reorder"
                                {...listeners}
                                {...attributes}
                              >
                                <GripIcon />
                              </span>
                              <ChevronSvg />
                              <span className={s.avatar}>
                                {getInitials(watchedTeamDeparted[i]?.name ?? field.name)}
                              </span>
                              <span className={s.itemName}>
                                {watchedTeamDeparted[i]?.name ?? field.name}
                              </span>
                              {(() => {
                                const rolePart =
                                  ROLE_LABELS[watchedTeamDeparted[i]?.role ?? field.role];
                                const firmJoined =
                                  watchedTeamDeparted[i]?.firmJoined ?? field.firmJoined;
                                const firmPart = firmJoined ? `Left for ${firmJoined}` : undefined;
                                const parts = [rolePart, firmPart].filter(Boolean);
                                return parts.length > 0 ? (
                                  <span className={s.itemSubtitle}>{parts.join(" · ")}</span>
                                ) : null;
                              })()}
                              <StatusBadge status={status} />
                            </div>
                          </summary>
                          <div className={s.accordionBody}>
                            <div className={s.fieldGrid}>
                              <div className={s.field}>
                                <label className={s.fieldLabel}>Name</label>
                                <Controller
                                  control={control}
                                  name={`teamDeparted.${i}.name`}
                                  render={({ field: f }) => (
                                    <Input className={s.cellInput} {...f} />
                                  )}
                                />
                              </div>
                              <div className={s.field}>
                                <label className={s.fieldLabel}>Role</label>
                                <Controller
                                  control={control}
                                  name={`teamDeparted.${i}.role`}
                                  render={({ field: f }) => (
                                    <Select
                                      className={s.cellSelect}
                                      options={ROLE_OPTIONS}
                                      size="small"
                                      {...f}
                                    />
                                  )}
                                />
                              </div>
                              <div className={s.field}>
                                <label className={s.fieldLabel}>Job title</label>
                                <Controller
                                  control={control}
                                  name={`teamDeparted.${i}.jobTitle`}
                                  render={({ field: f }) => (
                                    <Input className={s.cellInput} {...f} />
                                  )}
                                />
                              </div>
                              <div className={s.field}>
                                <label className={s.fieldLabel}>Date left</label>
                                <Controller
                                  control={control}
                                  name={`teamDeparted.${i}.dateLeft`}
                                  render={({ field: f }) => (
                                    <Input
                                      className={s.cellInput}
                                      type="date"
                                      value={f.value ?? ""}
                                      onChange={e => f.onChange(e.target.value || null)}
                                      onBlur={f.onBlur}
                                    />
                                  )}
                                />
                              </div>
                              <div className={s.field}>
                                <label className={s.fieldLabel}>Firm joined</label>
                                <Controller
                                  control={control}
                                  name={`teamDeparted.${i}.firmJoined`}
                                  render={({ field: f }) => (
                                    <Input
                                      className={s.cellInput}
                                      value={f.value ?? ""}
                                      onChange={e => f.onChange(e.target.value || null)}
                                      onBlur={f.onBlur}
                                    />
                                  )}
                                />
                              </div>
                              <div className={s.footerRow}>
                                <AuditBlock
                                  created={field.created}
                                  modified={field.modified}
                                  modifiedBy={field.modifiedBy}
                                />
                                <button
                                  type="button"
                                  className={s.removeBtn}
                                  onClick={() =>
                                    setPendingRemove({
                                      section: "teamDeparted",
                                      index: i,
                                      label: field.name || "this lawyer",
                                    })
                                  }
                                >
                                  Remove
                                </button>
                                <span className={s.statusLabel}>Status</span>
                                <Controller
                                  control={control}
                                  name={`teamDeparted.${i}.status`}
                                  render={({ field: f }) => (
                                    <Select
                                      className={`${s.statusSelect} ${STATUS_SELECT_CLASS[f.value] ?? ""}`}
                                      options={STATUS_OPTIONS}
                                      size="small"
                                      {...f}
                                    />
                                  )}
                                />
                              </div>
                            </div>
                          </div>
                        </details>
                      )}
                    </SortableAccordionItem>
                  );
                })}
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* -- Key Clients -- */}
        <div className={s.sectionCard}>
          <div className={s.sectionHeader}>
            <h3 className={s.sectionTitle}>Key Clients</h3>
            <span className={s.countBadge}>{keyClientsFields.length}</span>
            {getPendingCount(watchedKeyClients) > 0 && (
              <span className={s.pendingText}>
                &middot; {getPendingCount(watchedKeyClients)} pending
              </span>
            )}
            <button
              type="button"
              className={s.addBtn}
              onClick={() => {
                const newId = crypto.randomUUID();
                appendKeyClient({
                  id: newId,
                  companyName: "",
                  industry: "",
                  engagementDate: null,
                  publishable: true,
                  created: new Date().toISOString(),
                  modified: null,
                  modifiedBy: null,
                  status: "pending",
                });
                setManualDirty(true);
                setNewItemId(newId);
              }}
            >
              + Add client
            </button>
          </div>
          {keyClientsFields.length === 0 ? (
            <p className={s.emptySection}>No key clients in this submission</p>
          ) : (
            <DndContext
              sensors={dragSensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEndKeyClients}
            >
              <SortableContext
                items={keyClientsFields.map(f => f.id)}
                strategy={verticalListSortingStrategy}
              >
                {keyClientsFields.map((field, i) => {
                  const isPending = watchedKeyClients[i]?.status === "pending";
                  const status = watchedKeyClients[i]?.status ?? "draft";
                  return (
                    <SortableAccordionItem key={field.id} id={field.id}>
                      {({ listeners, attributes }) => (
                        <details
                          className={`${s.accordion} ${isPending ? s.accordionPending : ""}`}
                          data-item-id={watchedKeyClients[i]?.id ?? field.id}
                          {...(isPending ? { open: true } : {})}
                        >
                          <summary>
                            <div className={s.accordionSummary}>
                              <span
                                className={s.dragHandle}
                                title="Drag to reorder"
                                {...listeners}
                                {...attributes}
                              >
                                <GripIcon />
                              </span>
                              <ChevronSvg />
                              <span className={`${s.avatar} ${s.avatarSquare}`}>
                                {getInitials(
                                  watchedKeyClients[i]?.companyName ?? field.companyName,
                                )}
                              </span>
                              <span className={s.itemName}>
                                {watchedKeyClients[i]?.companyName ?? field.companyName}
                              </span>
                              {(() => {
                                const industryId = watchedKeyClients[i]?.industry || field.industry;
                                const industryLabel = industryId
                                  ? industryOptions.find(o => o.value === industryId)?.label
                                  : undefined;
                                const parts = [
                                  industryLabel,
                                  (watchedKeyClients[i]?.publishable ?? field.publishable)
                                    ? "Publishable"
                                    : "Not publishable",
                                ].filter(Boolean);
                                return parts.length > 0 ? (
                                  <span className={s.itemSubtitle}>{parts.join(" · ")}</span>
                                ) : null;
                              })()}
                              <StatusBadge status={status} />
                            </div>
                          </summary>
                          <div className={s.accordionBody}>
                            <div className={s.fieldGrid}>
                              <div className={`${s.field} ${s.fieldSpan2}`}>
                                <label className={s.fieldLabel}>Company name</label>
                                <Controller
                                  control={control}
                                  name={`keyClients.${i}.companyName`}
                                  render={({ field: f }) => (
                                    <Input className={s.cellInput} {...f} />
                                  )}
                                />
                              </div>
                              <div className={s.field}>
                                <label className={s.fieldLabel}>Industry</label>
                                <Controller
                                  control={control}
                                  name={`keyClients.${i}.industry`}
                                  render={({ field: f }) => (
                                    <SearchableSelect
                                      name={f.name}
                                      label=""
                                      placeholder="Select industry/sector"
                                      options={industryOptions}
                                      value={f.value || null}
                                      onChange={val => f.onChange(val ?? "")}
                                    />
                                  )}
                                />
                              </div>
                              <div className={s.field}>
                                <label className={s.fieldLabel}>Engagement date</label>
                                <Controller
                                  control={control}
                                  name={`keyClients.${i}.engagementDate`}
                                  render={({ field: f }) => (
                                    <Input
                                      className={s.cellInput}
                                      type="date"
                                      value={f.value ?? ""}
                                      onChange={e => f.onChange(e.target.value || null)}
                                      onBlur={f.onBlur}
                                    />
                                  )}
                                />
                              </div>
                              <div className={s.field}>
                                <label className={s.fieldLabel}>Publishable</label>
                                <Controller
                                  control={control}
                                  name={`keyClients.${i}.publishable`}
                                  render={({ field: f }) => (
                                    <Select
                                      className={s.cellSelect}
                                      options={PUBLISHABLE_OPTIONS}
                                      size="small"
                                      value={f.value ? "yes" : "no"}
                                      onChange={e =>
                                        f.onChange((e.target as HTMLSelectElement).value === "yes")
                                      }
                                      onBlur={f.onBlur}
                                    />
                                  )}
                                />
                              </div>
                              <div className={s.footerRow}>
                                <AuditBlock
                                  created={field.created}
                                  modified={field.modified}
                                  modifiedBy={field.modifiedBy}
                                />
                                <button
                                  type="button"
                                  className={s.removeBtn}
                                  onClick={() =>
                                    setPendingRemove({
                                      section: "keyClients",
                                      index: i,
                                      label: field.companyName || "this client",
                                    })
                                  }
                                >
                                  Remove
                                </button>
                                <span className={s.statusLabel}>Status</span>
                                <Controller
                                  control={control}
                                  name={`keyClients.${i}.status`}
                                  render={({ field: f }) => (
                                    <Select
                                      className={`${s.statusSelect} ${STATUS_SELECT_CLASS[f.value] ?? ""}`}
                                      options={STATUS_OPTIONS}
                                      size="small"
                                      {...f}
                                    />
                                  )}
                                />
                              </div>
                            </div>
                          </div>
                        </details>
                      )}
                    </SortableAccordionItem>
                  );
                })}
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* -- Work Highlights -- */}
        <div className={s.sectionCard}>
          <div className={s.sectionHeader}>
            <h3 className={s.sectionTitle}>Work Highlights</h3>
            <span className={s.countBadge}>{workHighlightsFields.length}</span>
            {getPendingCount(watchedWorkHighlights) > 0 && (
              <span className={s.pendingText}>
                &middot; {getPendingCount(watchedWorkHighlights)} pending
              </span>
            )}
            <button
              type="button"
              className={s.addBtn}
              onClick={() => {
                const newId = crypto.randomUUID();
                appendWorkHighlight({
                  id: newId,
                  client: "",
                  summary: "",
                  date: null,
                  created: new Date().toISOString(),
                  modified: null,
                  modifiedBy: null,
                  status: "pending",
                });
                setManualDirty(true);
                setNewItemId(newId);
              }}
            >
              + Add highlight
            </button>
          </div>
          {workHighlightsFields.length === 0 ? (
            <p className={s.emptySection}>No work highlights in this submission</p>
          ) : (
            <DndContext
              sensors={dragSensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEndWorkHighlights}
            >
              <SortableContext
                items={workHighlightsFields.map(f => f.id)}
                strategy={verticalListSortingStrategy}
              >
                {workHighlightsFields.map((field, i) => {
                  const isPending = watchedWorkHighlights[i]?.status === "pending";
                  const status = watchedWorkHighlights[i]?.status ?? "draft";
                  return (
                    <SortableAccordionItem key={field.id} id={field.id}>
                      {({ listeners, attributes }) => (
                        <details
                          className={`${s.accordion} ${isPending ? s.accordionPending : ""}`}
                          data-item-id={watchedWorkHighlights[i]?.id ?? field.id}
                          {...(isPending ? { open: true } : {})}
                        >
                          <summary>
                            <div className={s.accordionSummary}>
                              <span
                                className={s.dragHandle}
                                title="Drag to reorder"
                                {...listeners}
                                {...attributes}
                              >
                                <GripIcon />
                              </span>
                              <ChevronSvg />
                              <span className={`${s.avatar} ${s.avatarSquare}`}>
                                {getInitials(
                                  (watchedWorkHighlights[i]?.client ?? field.client) || "WH",
                                )}
                              </span>
                              <span className={s.itemName}>
                                {(watchedWorkHighlights[i]?.client ?? field.client) || "—"}
                              </span>
                              <span className={s.itemSubtitle}>
                                {(watchedWorkHighlights[i]?.summary ?? field.summary)
                                  ? (() => {
                                      const raw =
                                        watchedWorkHighlights[i]?.summary ?? field.summary;
                                      const doc = new DOMParser().parseFromString(raw, "text/html");
                                      const text = doc.body.textContent ?? "";
                                      return text.length > 80 ? `${text.slice(0, 80)}…` : text;
                                    })()
                                  : "—"}
                              </span>
                              <StatusBadge status={status} />
                            </div>
                          </summary>
                          <div className={s.accordionBody}>
                            <div className={s.fieldGridHighlight}>
                              <div className={`${s.field} ${s.fieldRowSpan3}`}>
                                <label className={s.fieldLabel}>Summary</label>
                                <Controller
                                  control={control}
                                  name={`workHighlights.${i}.summary`}
                                  render={({ field: f }) => (
                                    <RichTextEditor
                                      value={f.value ?? ""}
                                      onChange={f.onChange}
                                      preset="basic"
                                      minHeight={110}
                                    />
                                  )}
                                />
                              </div>
                              <div className={s.field}>
                                <label className={s.fieldLabel}>Client</label>
                                <Controller
                                  control={control}
                                  name={`workHighlights.${i}.client`}
                                  render={({ field: f }) => (
                                    <Input
                                      className={s.cellInput}
                                      {...f}
                                      placeholder="Client name"
                                    />
                                  )}
                                />
                              </div>
                              <div className={s.field}>
                                <label className={s.fieldLabel}>Date</label>
                                <Controller
                                  control={control}
                                  name={`workHighlights.${i}.date`}
                                  render={({ field: f }) => (
                                    <Input
                                      className={s.cellInput}
                                      type="date"
                                      value={f.value ?? ""}
                                      onChange={e => f.onChange(e.target.value || null)}
                                      onBlur={f.onBlur}
                                    />
                                  )}
                                />
                              </div>
                              <div className={s.footerRow}>
                                <AuditBlock
                                  created={field.created}
                                  modified={field.modified}
                                  modifiedBy={field.modifiedBy}
                                />
                                <button
                                  type="button"
                                  className={s.removeBtn}
                                  onClick={() =>
                                    setPendingRemove({
                                      section: "workHighlights",
                                      index: i,
                                      label: field.client || "this highlight",
                                    })
                                  }
                                >
                                  Remove
                                </button>
                                <span className={s.statusLabel}>Status</span>
                                <Controller
                                  control={control}
                                  name={`workHighlights.${i}.status`}
                                  render={({ field: f }) => (
                                    <Select
                                      className={`${s.statusSelect} ${STATUS_SELECT_CLASS[f.value] ?? ""}`}
                                      options={STATUS_OPTIONS}
                                      size="small"
                                      {...f}
                                    />
                                  )}
                                />
                              </div>
                            </div>
                          </div>
                        </details>
                      )}
                    </SortableAccordionItem>
                  );
                })}
              </SortableContext>
            </DndContext>
          )}
        </div>

        <div className={s.actionBar}>
          {!isCreate && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              type="button"
              onClick={() => setShowDeleteModal(true)}
              disabled={isSaving}
            >
              Delete submission
            </Button>
          )}
          <div className={s.actionBarRight}>
            {isDirty || manualDirty ? (
              <span className={s.dirtyHint}>Unsaved changes</span>
            ) : savedOnce ? (
              <span className={s.savedHint}>All changes saved</span>
            ) : null}
            <Button
              size="small"
              variant="contained"
              color="primary"
              type="submit"
              disabled={isSaving || (!isDirty && !manualDirty)}
            >
              {isSaving ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        </div>
      </form>

      {pendingNav && (
        <UnsavedChangesModal
          onClose={() => setPendingNav(null)}
          onConfirm={() => {
            const go = pendingNav;
            setPendingNav(null);
            go();
          }}
        />
      )}

      {pendingRemove && (
        <ConfirmationModal
          title="Remove item"
          message={`Are you sure you want to remove "${pendingRemove.label}"?`}
          warningMessage="This will remove the item from this submission. You can undo this by not saving your changes."
          confirmButtonText="Remove"
          confirmButtonColor="error"
          cancelButtonText="Cancel"
          onClose={() => setPendingRemove(null)}
          onConfirm={async () => confirmRemove()}
        />
      )}

      {showDeleteModal && !isCreate && (
        <ConfirmationModal
          title="Delete rolling submission"
          message={`Permanently delete the rolling submission for ${submission?.firmName}?`}
          warningMessage="This removes all lawyers, clients and work highlights and cannot be undone."
          confirmButtonText="Delete submission"
          confirmButtonColor="error"
          cancelButtonText="Cancel"
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
        />
      )}

      {showPublishPrompt && (
        <ConfirmationModal
          title="Publish submission?"
          message="All fields have been set to Publish. Would you like to set the overall submission status to Published as well?"
          warningMessage=""
          confirmButtonText="Yes, publish and save"
          confirmButtonColor="default"
          cancelButtonText="No, save as-is"
          onClose={() => {
            setShowPublishPrompt(false);
            void handleSubmit(onSubmit)();
          }}
          onConfirm={async () => {
            setValue("submissionStatus", "publish", { shouldDirty: true });
            setShowPublishPrompt(false);
            void handleSubmit(onSubmit)();
          }}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={toast.type === "error" ? 5000 : 3000}
        />
      )}
    </div>
  );
}

export default RollingSubmissionDetail;
